import { useEffect, useRef } from "preact/hooks";
import {
  listById,
  listLabel,
  newId,
  required,
  showError,
  showNotice,
  styled,
  ToucanSearchField,
} from "toucan";
import { AlbumResult } from "../components/AlbumResult.tsx";
import { chosenShelfId, defaultShelfId } from "../library.ts";
import { addFoundAlbum, setAlbumShelf } from "../model.ts";
import type { FoundAlbum } from "../model.ts";
import { go } from "../routes.ts";
import { useAlbumSearch } from "../searching.ts";
import { store } from "../state.ts";

const Page = styled("section")`
  /* The field sticks to the top while the page scrolls the results under it,
     so the words can be changed from anywhere in a long list. */
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: var(--toucan-canvas);

  .field {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 1rem;
    background: var(--toucan-canvas);
    border-bottom: 1px solid var(--pico-muted-border-color);
  }

  /* As wide as the results below it, so they share both edges. Set on the
     whole field, since its clear button sits against the field's own right
     edge. */
  .field .search {
    max-width: 28rem;
    margin: 0 auto;
  }

  .results {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
    max-width: 30rem;
    margin: 0 auto;
    padding: 0.75rem 1rem 1rem;
  }

  .note {
    margin: 0;
    padding: 0.5rem 0;
    color: var(--toucan-muted);
  }
`;

/**
 * Finding an album and adding it to the library, as a page so the results
 * have the whole screen.
 *
 * Picking one files it on the shelf the library was left on and opens the
 * record's page. An album already in the library opens the same page with a
 * message saying so, and, picked from inside a shelf, moves onto that shelf.
 *
 * `onAdded` names the record just added, so its page opens ready for a note.
 */
export function SearchPage({
  onAdded,
}: {
  onAdded: (albumId: string) => void;
}) {
  const search = useAlbumSearch("");
  const field = useRef<HTMLInputElement>(null);
  // Focused here rather than by `autofocus`, which a document honours only
  // once, and the app's first screen has already used it.
  useEffect(() => {
    required(field.current, "the search field").focus();
  }, []);
  const add = (found: FoundAlbum): void => {
    const before = store().albums.length;
    const album = addFoundAlbum(
      store(),
      found,
      Date.now(),
      newId(),
      defaultShelfId(),
    );
    if (store().albums.length === before) {
      // Picked from inside a shelf, the album moves there, and the message
      // says so since the page looks the same either way.
      const shelfId = chosenShelfId();
      if (shelfId !== null && album.shelfId !== shelfId) {
        setAlbumShelf(album, shelfId);
        const shelf = required(listById(store().shelves, shelfId), "shelf");
        showNotice(
          `${found.name} was already in the library, and is now on ${listLabel(shelf)}.`,
        );
      } else {
        showError(`${found.name} is already in the library.`);
      }
    } else {
      onAdded(album.id);
    }
    go(`album/${album.id}`);
  };
  return (
    <Page>
      <div class="field">
        <ToucanSearchField
          class="search"
          inputRef={field}
          label="Search Spotify for an album"
          placeholder="Search Spotify…"
          query={search.query}
          onQuery={search.type}
        />
      </div>
      <div class="results">
        {search.note !== "" && <p class="note">{search.note}</p>}
        {search.results.map((found) => (
          <AlbumResult
            key={found.id}
            name={found.name}
            artist={found.artist}
            image={found.image}
            onPick={() => {
              add(found);
            }}
          />
        ))}
      </div>
    </Page>
  );
}
